import { Select } from 'foundations-components/transitional/components/Select';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { InputGroup } from 'foundations-components/transitional/components/InputGroup';
import { List, fromJS } from 'immutable';
import { useCallback, useMemo } from 'react';
import { isSucceeded } from 'reporting-data/asyncData/AsyncData';
import { GAUGE } from 'reporting-data/constants/chartTypes';
import { AGGREGATION, TIME_SERIES } from 'reporting-data/constants/configTypes';
import { ACCUMULATE } from 'reporting-data/constants/processorTypes';
import { DATE, DATE_TIME } from 'reporting-data/constants/property-types';
import useFetchPropertyMetaDataForDataType from 'reporting-data/hooks/useFetchPropertyMetaDataForDataType';
import { getCompare, getDataType, getDateRangeFilterValue, getDimensions, getFrequency, getReportConfig } from 'reporting-data/report/configReportGetters';
import { setCompare, setDimensions, setFrequency } from 'reporting-data/report/configReportSetters';
import { getReportChartType } from 'reporting-data/report/reportGetters';
import useFetchPropertyOptionsForDimensionEditorDropdown from './useFetchPropertyOptionsForDimensionEditorDropdown';
// @ts-expect-error Untyped import
import getDefaultFrequency from 'reporting-ui-components/lib/getDefaultFrequency';
import getMeasureSizeRanges from '../../../lib/report-utils/getMeasureSizeRanges';
import { flattenPropertyGroups } from '../../../lib/reportPropertyUtils';
import DataColumnsHintFooter from '../../shared-components/dropdown/DataColumnsHintFooter';
import OptionWithTooltip from '../../shared-components/dropdown/OptionWithTooltip';
import { disableUISelectOptionForDimensionOrBreakdownDropdown, isCompareValid } from '../utils';
import { shouldHideNoneOption } from '../../../lib/shared-utils';
import { jsx as _jsx } from "react/jsx-runtime";
const getCustomOptionsFormatter = breakdown => options => {
  const disabledExtraDimensionOptions = disableUISelectOptionForDimensionOrBreakdownDropdown(options, breakdown);
  return flattenPropertyGroups(disabledExtraDimensionOptions);
};
const ConfigReportDimensionEditorDropdown = props => {
  var _getDimensions;
  const {
    report,
    dropdownFooter = /*#__PURE__*/_jsx(DataColumnsHintFooter, {}),
    onChange,
    hideClearButton = false,
    buttonUse
  } = props;
  const chartType = getReportChartType(report);
  const breakdownValue = getDimensions(report).get(1);
  const {
    minDimensions
  } = getMeasureSizeRanges(report);
  const hideNoneOptionInUISelect = shouldHideNoneOption({
    report,
    minDimensions,
    hideClearButton
  });
  const customDimensionsOptionsFormatter = useMemo(() => {
    return getCustomOptionsFormatter(breakdownValue);
  }, [breakdownValue]);
  const dimensionOptions = useFetchPropertyOptionsForDimensionEditorDropdown(report, customDimensionsOptionsFormatter);
  const propertyMetadataFetch = useFetchPropertyMetaDataForDataType(getDataType(report));
  const propertyMetadata = isSucceeded(propertyMetadataFetch) ? propertyMetadataFetch.data : fromJS([]);
  const dimensionValue = dimensionOptions.length ? (_getDimensions = getDimensions(report)) === null || _getDimensions === void 0 ? void 0 : _getDimensions.first() : undefined;
  const onDimensionChange = useCallback(({
    target: {
      value
    }
  }) => {
    let updatedReport = report;
    const dateRange = getDateRangeFilterValue(getReportConfig(report));
    const defaultFrequency = getDefaultFrequency(dateRange.toJS());
    const isTimeSeriesDimension = [DATE, DATE_TIME].includes(propertyMetadata.getIn([value, 'type']));
    updatedReport = updatedReport.withMutations(mutableReport => {
      mutableReport.setIn(['config', 'configType'], isTimeSeriesDimension ? TIME_SERIES : AGGREGATION).set('config', setDimensions(getReportConfig(mutableReport), getDimensions(mutableReport).set(0, value)));
      if (!value) {
        mutableReport.deleteIn(['config', 'dimensions', 0]);
      }
      if (!isTimeSeriesDimension) {
        const config = mutableReport.get('config');
        const processors = config.get('processors', List()).filter(processor => processor !== ACCUMULATE);
        mutableReport.setIn(['config', 'processors'], processors);
      }
      const updatedFrequencyValue = isTimeSeriesDimension ? getFrequency(getReportConfig(mutableReport)) || defaultFrequency : undefined;
      if (updatedFrequencyValue) {
        mutableReport.set('config', setFrequency(getReportConfig(mutableReport), updatedFrequencyValue));
      }
      mutableReport.set('config', setCompare(getReportConfig(mutableReport), isCompareValid(mutableReport) ? getCompare(getReportConfig(mutableReport)) : undefined));
    });
    onChange(updatedReport);
  }, [onChange, report, propertyMetadata]);
  return /*#__PURE__*/_jsx(InputGroup, {
    label: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "report-customization-components.customization-components.dimension-editor-dropdown.config.label"
    }),
    tooltip: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "report-customization-components.customization-components.dimension-editor-dropdown.config.tooltip"
    }),
    use: "itemRight",
    children: /*#__PURE__*/_jsx("div", {
      style: {
        width: '100%'
      },
      children: /*#__PURE__*/_jsx(Select, Object.assign({}, props, {
        clearable: !hideNoneOptionInUISelect,
        itemComponent: OptionWithTooltip,
        dropdownFooter: dropdownFooter,
        buttonSize: "md",
        options: dimensionOptions,
        value: dimensionValue,
        onChange: onDimensionChange,
        disabled: chartType === GAUGE,
        paddingInlineStart: buttonUse === 'transparent' ? '0' : undefined,
        menuWidth: "auto",
        placeholder: I18n.text('report-customization-components.constants.uiselect.none')
      }))
    })
  });
};
export default ConfigReportDimensionEditorDropdown;